<template>
  <div class="container mt-4">
    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
      <h1 class="resumen-title mb-0">Resumen</h1>
      <button
        v-if="isAdmin"
        type="button"
        class="btn btn-sm btn-outline-secondary"
        :disabled="sincronizandoServicios"
        title="Ejecuta la misma sincronización periódica: suscripciones, FitDays, Oura, Coros y Finanzas"
        @click="sincronizarServicios"
      >
        <span
          v-if="sincronizandoServicios"
          class="spinner-border spinner-border-sm me-1"
          role="status"
          aria-hidden="true"
        ></span>
        <i v-else class="bi bi-arrow-repeat me-1"></i>
        {{ sincronizandoServicios ? 'Sincronizando…' : 'Sincronizar servicios' }}
      </button>
    </div>

    <div
      v-if="isAdmin && mensajeSync"
      class="alert py-2 small mb-3"
      :class="mensajeSyncTipo === 'error' ? 'alert-warning' : 'alert-success'"
      role="alert"
    >
      {{ mensajeSync }}
    </div>

    <ResumenAdminPaneles v-if="isAdmin" />

    <FrasesDelDia v-if="isAdmin" class="mb-4" compact />

    <section v-if="hasIngresos" class="resumen-hero">
      <p class="resumen-kicker">Balance del mes</p>
      <p class="resumen-cifra" :class="balanceMesTextClass">{{ balanceMes }} €</p>
      <p class="resumen-hero-sub">
        Ingresos <span class="cifra-ingreso">{{ totalIngresosMes }} €</span>
        <span aria-hidden="true"> · </span>
        Gastos <span class="cifra-gasto">{{ totalMes }} €</span>
      </p>
    </section>
    <section v-else class="resumen-hero">
      <p class="resumen-kicker">Gastos del mes</p>
      <p class="resumen-cifra cifra-gasto">{{ totalMes }} €</p>
      <p class="resumen-hero-sub">
        Puntuales {{ totalMesPuntual }} € · Suscripciones {{ totalMesSuscripciones }} €
      </p>
    </section>

    <div class="row">
      <!-- Resumen de Gastos (móvil con ingresos: tras Ingresos, antes de Balance) -->
      <div class="col-md-6 mb-4" :class="clasesOrdenResumen.gastos">
        <div class="card resumen-card resumen-card--gastos">
          <div class="card-body">
            <h3 class="resumen-bloque-titulo">Gastos</h3>
            <div class="list-group">
              <div class="list-group-item d-flex flex-column"
                   @click="mostrarDesgloseGastosMes"
                   style="cursor: pointer;"
                   data-bs-toggle="tooltip" 
                   data-bs-placement="left" 
                   title="Haz clic para ver el desglose de gastos del mes actual">
                <div class="d-flex justify-content-between align-items-center">
                  <span>Total del mes</span>
                  <span class="cifra-gasto resumen-importe">{{ totalMes }} €</span>
                </div>
                <div class="small mt-2 ps-1" style="color: #495057; font-size: 0.85rem;">
                  <strong>Puntuales:</strong> {{ totalMesPuntual }}€ · <strong>Suscripciones:</strong> {{ totalMesSuscripciones }}€
                </div>
              </div>
              <div class="list-group-item">
                <div class="d-flex justify-content-between align-items-center mb-2"
                     @click="() => mostrarDesgloseGastosMesPasado()"
                     style="cursor: pointer;"
                     data-bs-toggle="tooltip" 
                     data-bs-placement="left" 
                     :title="`Haz clic para ver el desglose de gastos de ${etiquetaMesComparativa}`">
                  <div class="d-flex align-items-center gap-2 flex-wrap">
                    <input
                      v-if="isAdmin"
                      type="month"
                      class="form-control form-control-sm mes-comparativa-input"
                      v-model="mesComparativa"
                      @click.stop
                      @change.stop
                      title="Seleccionar mes para comparar"
                    />
                    <small>
                      {{ etiquetaMesComparativa }} (hasta día {{ diaActual }})
                    </small>
                  </div>
                  <span class="fw-semibold" :class="getTrendTextClass(totalMesPasado, totalMes)">
                    {{ totalMesPasado }} €
                    <span class="resumen-delta">{{ textoVariacion(totalMes, totalMesPasado) }}</span>
                  </span>
                </div>
                <div class="small mb-1 ps-1" style="color: #495057; font-size: 0.85rem;">
                  <strong>Puntuales:</strong> {{ totalMesPasadoPuntual }}€ · <strong>Suscripciones:</strong> {{ totalMesPasadoSuscripciones }}€
                </div>
              </div>
              <div class="list-group-item">
                <div class="d-flex justify-content-between align-items-center mb-2"
                     @click="() => mostrarDesgloseGastosMesAnoPasado()"
                     style="cursor: pointer;"
                     data-bs-toggle="tooltip" 
                     data-bs-placement="left" 
                     title="Haz clic para ver el desglose de gastos del mismo mes del año pasado">
                  <small>
                    Mismo mes año pasado (hasta día {{ diaActual }})
                  </small>
                  <span class="fw-semibold" :class="getTrendTextClass(totalMesAnoPasado, totalMes)">
                    {{ totalMesAnoPasado }} €
                    <span class="resumen-delta">{{ textoVariacion(totalMes, totalMesAnoPasado) }}</span>
                  </span>
                </div>
                <div class="small mb-1 ps-1" style="color: #495057; font-size: 0.85rem;">
                  <strong>Puntuales:</strong> {{ totalMesAnoPasadoPuntual }}€ · <strong>Suscripciones:</strong> {{ totalMesAnoPasadoSuscripciones }}€
                </div>
              </div>
              <div class="list-group-item border-top border-2"></div>
              <div class="list-group-item d-flex justify-content-between align-items-center"
                   @click="mostrarDesgloseGastosAno"
                   style="cursor: pointer;"
                   data-bs-toggle="tooltip" 
                   data-bs-placement="left" 
                   title="Incluye los gastos de los últimos 12 meses completos">
                <span>Último año natural</span>
                <span class="resumen-total-ano">
                  {{ totalAno }} €
                  <small class="text-muted fw-normal">({{ totalAnoMensual }} €/mes)</small>
                </span>
              </div>
              <div class="list-group-item">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <small>Periodo anterior equivalente ({{ rangoAnoPasadoFormateado }}):</small>
                  <span class="fw-semibold" :class="getTrendTextClass(totalAnoPasado, totalAno)">
                    {{ totalAnoPasado }} €
                    <span class="resumen-delta">{{ textoVariacion(totalAno, totalAnoPasado) }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Resumen de Suscripciones -->
      <div class="col-md-6 mb-4" :class="clasesOrdenResumen.suscripciones">
        <div class="card">
          <div class="card-body">
            <h3 class="resumen-bloque-titulo">Suscripciones</h3>
            <div class="list-group">
              <div class="list-group-item d-flex justify-content-between align-items-center"
                   @click="mostrarDesgloseSuscripciones"
                   style="cursor: pointer;"
                   data-bs-toggle="tooltip" 
                   data-bs-placement="left" 
                   title="Haz clic para ver el desglose de suscripciones ordenadas por importe">
                <span>Total mensual</span>
                <span class="cifra-gasto resumen-importe">{{ totalMensualSuscripciones }} €</span>
              </div>
              <div class="list-group-item">
                <div class="d-flex justify-content-between align-items-center">
                  <small>Suscripciones mensuales:</small>
                  <span>{{ totalSuscripcionesMensuales }}€</span>
                </div>
              </div>
              <div class="list-group-item">
                <div class="d-flex justify-content-between align-items-center">
                  <small>Prorrateo anuales:</small>
                  <span>{{ totalSuscripcionesAnuales }}€</span>
                </div>
              </div>
              <div class="list-group-item">
                <div class="d-flex justify-content-between align-items-center">
                  <small>Prorrateo trimestrales:</small>
                  <span>{{ totalSuscripcionesTrimestrales }}€</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Ingresos (solo importes; móvil: primero si hay módulo ingresos) -->
      <div class="col-md-6 mb-4" v-if="hasIngresos" :class="clasesOrdenResumen.ingresos">
        <div class="card resumen-card resumen-card--ingresos">
          <div class="card-body">
            <h3 class="resumen-bloque-titulo">Ingresos</h3>
            <div class="list-group">
              <div class="list-group-item d-flex justify-content-between align-items-center">
                <span>Ingresos del mes</span>
                <span class="cifra-ingreso resumen-importe">{{ totalIngresosMes }} €</span>
              </div>
              <div class="list-group-item d-flex justify-content-between align-items-center">
                <span>Ingresos del año</span>
                <span class="cifra-ingreso resumen-importe">{{ totalIngresosAno }} €</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Balance (móvil: tercero, tras Gastos) -->
      <div class="col-md-6 mb-4" v-if="hasIngresos" :class="clasesOrdenResumen.balance">
        <div class="card resumen-card resumen-card--balance">
          <div class="card-body">
            <h3 class="resumen-bloque-titulo">Balance</h3>
            <div class="list-group">
              <div class="list-group-item d-flex justify-content-between align-items-center">
                <span class="fw-semibold">Balance del mes</span>
                <span class="fw-semibold" :class="balanceMesTextClass">{{ balanceMes }}€</span>
              </div>
              <div class="list-group-item d-flex justify-content-between align-items-center">
                <span class="fw-semibold">Balance del año</span>
                <span class="fw-semibold" :class="balanceAnoTextClass">{{ balanceAno }}€</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div id="resumen-seccion-fire"></div>

    <!-- Modal para mostrar el desglose de gastos -->
    <div class="modal fade" id="desgloseModal" tabindex="-1" aria-labelledby="desgloseModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="desgloseModalLabel">{{ tituloDesglose }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
          </div>
          <div class="modal-body">
            <div v-if="gastosDesgloseCompleto.length === 0" class="alert alert-info">
              No hay gastos para mostrar en este período.
            </div>
            <div v-else>
              <div class="mb-3">
                <label for="filtroCategoria" class="form-label">Filtrar por categoría:</label>
                <select id="filtroCategoria" class="form-select" v-model="filtroCategoria">
                  <option value="">Todas las categorías</option>
                  <option v-for="categoria in categoriasDisponibles" :key="categoria" :value="categoria">
                    {{ categoria }}
                  </option>
                </select>
              </div>
              <div v-if="modoDesglose === 'ultimoAnoNatural'" class="mb-3">
                <label for="ordenDesgloseAno" class="form-label">Ordenación:</label>
                <select id="ordenDesgloseAno" class="form-select" v-model="ordenDesgloseAno">
                  <option value="importe_desc">Importe: mayor a menor</option>
                  <option value="fecha_desc">Fecha: más reciente primero</option>
                  <option value="fecha_asc">Fecha: más antigua primero</option>
                </select>
              </div>
              <div class="table-responsive">
                <table class="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th v-if="modoDesglose !== 'suscripciones'">Fecha</th>
                      <th v-if="modoDesglose === 'suscripciones'">Día del mes</th>
                      <th>Concepto</th>
                      <th>Categoría</th>
                      <th v-if="modoDesglose === 'suscripciones'">Periodicidad</th>
                      <th v-if="modoDesglose === 'suscripciones'">Importe Original</th>
                      <th>{{ modoDesglose === 'suscripciones' ? 'Importe Mensual' : 'Cantidad' }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in gastosDesgloseFiltrados" :key="item.id">
                      <td v-if="modoDesglose !== 'suscripciones'">{{ formatDate(item.fecha) }}</td>
                      <td v-if="modoDesglose === 'suscripciones'">{{ item.dia_mes }}</td>
                      <td>{{ item.concepto }}</td>
                      <td>{{ item.categoria_nombre || 'Sin categoría' }}</td>
                      <td v-if="modoDesglose === 'suscripciones'">
                        {{ capitalizarPrimeraLetra(item.periodicidad) }}
                        <span v-if="item.tipo_division === 'mitad'" class="badge bg-info ms-1">Mitad</span>
                      </td>
                      <td v-if="modoDesglose === 'suscripciones'" class="text-end">{{ item.cantidad_original }}€</td>
                      <td class="text-end">{{ item.cantidad_total }}€</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="table-primary">
                      <td :colspan="modoDesglose === 'suscripciones' ? 5 : 3" class="text-end fw-bold">Total{{ filtroCategoria ? ' filtrado' : '' }}:</td>
                      <td class="text-end fw-bold">{{ totalDesgloseFiltrado }}€</td>
                    </tr>
                    <tr v-if="filtroCategoria" class="table-secondary">
                      <td :colspan="modoDesglose === 'suscripciones' ? 5 : 3" class="text-end">Total general:</td>
                      <td class="text-end">{{ totalDesglose }}€</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <div class="d-flex justify-content-between w-100">
              <div>
                <button 
                  v-if="(modoDesglose === 'mesPasado' || modoDesglose === 'mesAnoPasado') && !mostrandoMesCompleto" 
                  type="button" 
                  class="btn btn-primary" 
                  @click="mostrarDiasAdicionales">
                  <i class="bi bi-calendar-plus me-1"></i> Ver 10 días más
                </button>
                <span 
                  v-if="(modoDesglose === 'mesPasado' || modoDesglose === 'mesAnoPasado') && mostrandoMesCompleto" 
                  class="text-muted">
                  <i class="bi bi-info-circle me-1"></i> Mostrando el mes completo
                </span>
              </div>
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cerrar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import axios from '../config/axios';
import { esSuscripcionGastoUi } from '../utils/gastoClasificacion';
import ResumenAdminPaneles from '../components/ResumenAdminPaneles.vue';
import FrasesDelDia from '../components/FrasesDelDia.vue';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import * as bootstrap from 'bootstrap';


export default {
  name: 'ResumenView',
  components: { ResumenAdminPaneles, FrasesDelDia },
  
  setup() {
    const store = useStore();
    const gastos = computed(() => store.getters.getGastos || []);
    const suscripciones = ref([]);
    const error = ref(null);
    const hasIngresos = computed(() => store.getters['auth/hasIngresos']);
    const isAdmin = computed(() => !!store.getters['auth/isAdmin']);
    const sincronizandoServicios = ref(false);
    const mensajeSync = ref('');
    const mensajeSyncTipo = ref('ok');
    let mensajeSyncTimer = null;
    /** Móvil con ingresos: Ingresos → Gastos → Balance → Suscripciones; escritorio grid 2×2. */
    const clasesOrdenResumen = computed(() => {
      if (hasIngresos.value) {
        return {
          ingresos: 'order-1 order-md-3',
          gastos: 'order-2 order-md-1',
          balance: 'order-3 order-md-4',
          suscripciones: 'order-4 order-md-2'
        };
      }
      return {
        ingresos: '',
        gastos: '',
        balance: '',
        suscripciones: ''
      };
    });
    const ingresos = ref([]);
    const hoy = new Date();
    const diaActual = hoy.getDate();
    const mesActual = hoy.getMonth();
    const anoActual = hoy.getFullYear();

    const mesPasadoPorDefecto = mesActual === 0 ? 11 : mesActual - 1;
    const anoPasadoPorDefecto = mesActual === 0 ? anoActual - 1 : anoActual;
    const mesComparativaDefault = `${anoPasadoPorDefecto}-${String(mesPasadoPorDefecto + 1).padStart(2, '0')}`;
    const mesComparativa = ref(mesComparativaDefault);

    const parseMesComparativa = (valor) => {
      if (!valor || !/^\d{4}-\d{2}$/.test(valor)) {
        return { mes: mesPasadoPorDefecto, ano: anoPasadoPorDefecto };
      }
      const [ano, mes] = valor.split('-').map(Number);
      if (!ano || !mes || mes < 1 || mes > 12) {
        return { mes: mesPasadoPorDefecto, ano: anoPasadoPorDefecto };
      }
      return { mes: mes - 1, ano };
    };

    const mesComparativaParsed = computed(() => parseMesComparativa(mesComparativa.value));

    const etiquetaMesComparativa = computed(() => {
      const { mes, ano } = mesComparativaParsed.value;
      const esMesPasado = mes === mesPasadoPorDefecto && ano === anoPasadoPorDefecto;
      if (esMesPasado) return 'Mes pasado';
      const fecha = new Date(ano, mes, 1);
      const nombreMes = fecha.toLocaleString('es-ES', { month: 'long' });
      return `${nombreMes.charAt(0).toUpperCase()}${nombreMes.slice(1)} ${ano}`;
    });

    const gastoEnMesComparativa = (gasto, diasLimite = diaActual) => {
      if (!gasto?.fecha) return false;
      const { mes, ano } = mesComparativaParsed.value;
      const fecha = new Date(gasto.fecha);
      if (Number.isNaN(fecha.getTime())) return false;
      return fecha.getMonth() === mes &&
             fecha.getFullYear() === ano &&
             fecha.getDate() <= diasLimite;
    };
    
    // Variables para el desglose de gastos
    const tituloDesglose = ref('');
    const gastosDesgloseCompleto = ref([]);
    const totalDesglose = ref('0.00');
    const filtroCategoria = ref('');
    const ordenDesgloseAno = ref('importe_desc');
    let modalDesglose = null;
    
    // Variables para controlar el modo de desglose y días adicionales
    const modoDesglose = ref('');
    const diasAdicionalesActuales = ref(0);
    
    // Variable para controlar si se está mostrando el mes completo
    const mostrandoMesCompleto = computed(() => {
      if (modoDesglose.value !== 'mesPasado' && modoDesglose.value !== 'mesAnoPasado') {
        return false;
      }
      
      let ultimoDiaMes;
      
      if (modoDesglose.value === 'mesPasado') {
        const { mes, ano } = mesComparativaParsed.value;
        ultimoDiaMes = new Date(ano, mes + 1, 0).getDate();
      } else if (modoDesglose.value === 'mesAnoPasado') {
        const anoPasado = anoActual - 1;
        ultimoDiaMes = new Date(anoPasado, mesActual + 1, 0).getDate();
      } else {
        return false;
      }
      
      return diaActual + diasAdicionalesActuales.value >= ultimoDiaMes;
    });
    
    const obtenerFechaLocal = (fechaBase = new Date()) => {
      return new Date(fechaBase.getFullYear(), fechaBase.getMonth(), fechaBase.getDate());
    };

    const sumarDias = (fecha, dias) => {
      const resultado = new Date(fecha);
      resultado.setDate(resultado.getDate() + dias);
      return resultado;
    };

    const restarAnos = (fecha, anos) => {
      const resultado = new Date(fecha);
      resultado.setFullYear(resultado.getFullYear() - anos);
      return resultado;
    };

    const hoyLocal = obtenerFechaLocal(hoy);
    const finUltimoAnoNatural = sumarDias(hoyLocal, -1);
    const inicioUltimoAnoNatural = restarAnos(hoyLocal, 1);
    const inicioUltimoAnoNaturalAnterior = restarAnos(inicioUltimoAnoNatural, 1);
    const finUltimoAnoNaturalAnterior = sumarDias(inicioUltimoAnoNatural, -1);

    const gastoEnRango = (gasto, inicio, fin) => {
      const fechaRaw = new Date(gasto.fecha);
      const fecha = new Date(fechaRaw.getFullYear(), fechaRaw.getMonth(), fechaRaw.getDate());
      return fecha >= inicio && fecha <= fin;
    };

    const rangoAnoPasadoFormateado = computed(() => {
      return `${inicioUltimoAnoNaturalAnterior.toLocaleDateString('es-ES')} - ${finUltimoAnoNaturalAnterior.toLocaleDateString('es-ES')}`;
    });

    // Funciones de utilidad
    const getBadgeClass = (comparativo, actual) => {
      if (comparativo === 0 || actual === 0) return 'bg-info';
      if (parseFloat(comparativo) > parseFloat(actual)) return 'bg-success';
      if (parseFloat(comparativo) < parseFloat(actual)) return 'bg-danger';
      return 'bg-info';
    };

    const getProgressClass = (comparativo, actual) => {
      if (comparativo === 0 || actual === 0) return 'bg-info';
      if (parseFloat(comparativo) > parseFloat(actual)) return 'bg-success';
      if (parseFloat(comparativo) < parseFloat(actual)) return 'bg-danger';
      return 'bg-info';
    };

    const getProgressWidth = (comparativo, actual) => {
      if (actual === 0) return '0%';
      const porcentaje = (parseFloat(comparativo) / parseFloat(actual)) * 100;
      return `${Math.min(100, Math.max(0, porcentaje))}%`;
    };

    const getTrendTextClass = (comparativo, actual) => {
      if (comparativo === 0 || actual === 0) return 'text-muted';
      if (parseFloat(comparativo) > parseFloat(actual)) return 'text-success';
      if (parseFloat(comparativo) < parseFloat(actual)) return 'text-danger';
      return 'text-muted';
    };

    // Función para calcular el porcentaje
    const getPorcentaje = (comparativo, actual) => {
      if (actual === 0) return 0;
      const porcentaje = (parseFloat(comparativo) / parseFloat(actual)) * 100;
      return Math.round(porcentaje);
    };

    // Función para calcular el porcentaje inverso (actual respecto a comparativo)
    const getPorcentajeInverso = (actual, comparativo) => {
      if (comparativo === 0) return 0;
      const porcentaje = (parseFloat(actual) / parseFloat(comparativo)) * 100;
      return Math.round(porcentaje);
    };

    const textoVariacion = (actual, comparativo) => {
      const base = parseFloat(comparativo);
      const ahora = parseFloat(actual);
      if (!base) return '';
      const delta = Math.round(((ahora - base) / base) * 100);
      if (delta === 0) return '0 %';
      const signo = delta > 0 ? '+' : '−';
      return `${signo}${Math.abs(delta)} %`;
    };

    // Función para formatear fechas
    const formatDate = (date) => {
      if (!date) return 'No disponible';
      return new Date(date).toLocaleDateString('es-ES');
    };

    // Cálculos de ingresos
    const totalIngresosMes = computed(() => {
      return ingresos.value
        .filter(i => {
          const f = new Date(i.fecha);
          return f.getMonth() === mesActual && f.getFullYear() === anoActual;
        })
        .reduce((acc, i) => acc + parseFloat(i.cantidad), 0)
        .toFixed(2);
    });

    const totalIngresosAno = computed(() => {
      return ingresos.value
        .filter(i => new Date(i.fecha).getFullYear() === anoActual)
        .reduce((acc, i) => acc + parseFloat(i.cantidad), 0)
        .toFixed(2);
    });

    const balanceMes = computed(() => {
      return (parseFloat(totalIngresosMes.value) - parseFloat(totalMes.value)).toFixed(2);
    });

    const balanceAno = computed(() => {
      return (parseFloat(totalIngresosAno.value) - parseFloat(totalAno.value)).toFixed(2);
    });

    const balanceMesClass = computed(() => parseFloat(balanceMes.value) >= 0 ? 'bg-success' : 'bg-danger');
    const balanceAnoClass = computed(() => parseFloat(balanceAno.value) >= 0 ? 'bg-success' : 'bg-danger');
    const balanceMesTextClass = computed(() => (parseFloat(balanceMes.value) >= 0 ? 'text-success' : 'text-danger'));
    const balanceAnoTextClass = computed(() => (parseFloat(balanceAno.value) >= 0 ? 'text-success' : 'text-danger'));

    // Cálculos de gastos
    const totalAno = computed(() => {
      return gastos.value
        .filter(g =>
          gastoEnRango(g, inicioUltimoAnoNatural, finUltimoAnoNatural)
        )
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });

    const totalAnoPasado = computed(() => {
      return gastos.value
        .filter(g =>
          gastoEnRango(g, inicioUltimoAnoNaturalAnterior, finUltimoAnoNaturalAnterior)
        )
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });

    const totalAnoMensual = computed(() => {
      const total = parseFloat(totalAno.value) || 0;
      return (total / 12).toFixed(2);
    });

    const totalMes = computed(() => {
      return gastos.value
        .filter(g => {
          const fecha = new Date(g.fecha);
          return fecha.getMonth() === mesActual && 
                 fecha.getFullYear() === anoActual;
        })
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });

    const totalMesPasado = computed(() => {
      return gastos.value
        .filter(g => gastoEnMesComparativa(g))
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });

    const totalMesAnoPasado = computed(() => {
      const anoPasado = anoActual - 1;
      return gastos.value
        .filter(g => {
          const fecha = new Date(g.fecha);
          return fecha.getMonth() === mesActual && 
                 fecha.getFullYear() === anoPasado &&
                 fecha.getDate() <= diaActual;
        })
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });

    // Desglose puntual vs suscripciones: esSuscripcionGastoUi (script, override o categoría tipo suscripción)

    const totalMesPuntual = computed(() => {
      return gastos.value
        .filter(g => {
          const fecha = new Date(g.fecha);
          return fecha.getMonth() === mesActual && 
                 fecha.getFullYear() === anoActual &&
                 !esSuscripcionGastoUi(g);
        })
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });

    const totalMesSuscripciones = computed(() => {
      return gastos.value
        .filter(g => {
          const fecha = new Date(g.fecha);
          return fecha.getMonth() === mesActual && 
                 fecha.getFullYear() === anoActual &&
                 esSuscripcionGastoUi(g);
        })
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });

    const totalMesPasadoPuntual = computed(() => {
      return gastos.value
        .filter(g => gastoEnMesComparativa(g) && !esSuscripcionGastoUi(g))
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });

    const totalMesPasadoSuscripciones = computed(() => {
      return gastos.value
        .filter(g => gastoEnMesComparativa(g) && esSuscripcionGastoUi(g))
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });

    const totalMesAnoPasadoPuntual = computed(() => {
      const anoPasado = anoActual - 1;
      return gastos.value
        .filter(g => {
          const fecha = new Date(g.fecha);
          return fecha.getMonth() === mesActual && 
                 fecha.getFullYear() === anoPasado &&
                 fecha.getDate() <= diaActual &&
                 !esSuscripcionGastoUi(g);
        })
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });

    const totalMesAnoPasadoSuscripciones = computed(() => {
      const anoPasado = anoActual - 1;
      return gastos.value
        .filter(g => {
          const fecha = new Date(g.fecha);
          return fecha.getMonth() === mesActual && 
                 fecha.getFullYear() === anoPasado &&
                 fecha.getDate() <= diaActual &&
                 esSuscripcionGastoUi(g);
        })
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });

    // Cálculos de suscripciones
    const calcularCantidadMensual = (suscripcion) => {
      let cantidad = parseFloat(suscripcion.cantidad);
      if (suscripcion.tipo_division === 'mitad') {
        cantidad = cantidad / 2;
      }
      switch (suscripcion.periodicidad) {
        case 'anual':
          return cantidad / 12;
        case 'trimestral':
          return cantidad / 3;
        case 'mensual':
          return cantidad;
        default:
          return 0;
      }
    };

    const totalSuscripcionesMensuales = computed(() => {
      return suscripciones.value
        .filter(s => s.activa && s.periodicidad === 'mensual')
        .reduce((acc, s) => acc + calcularCantidadMensual(s), 0)
        .toFixed(2);
    });

    const totalSuscripcionesAnuales = computed(() => {
      return suscripciones.value
        .filter(s => s.activa && s.periodicidad === 'anual')
        .reduce((acc, s) => acc + calcularCantidadMensual(s), 0)
        .toFixed(2);
    });

    const totalSuscripcionesTrimestrales = computed(() => {
      return suscripciones.value
        .filter(s => s.activa && s.periodicidad === 'trimestral')
        .reduce((acc, s) => acc + calcularCantidadMensual(s), 0)
        .toFixed(2);
    });

    const totalMensualSuscripciones = computed(() => {
      return (
        parseFloat(totalSuscripcionesMensuales.value) +
        parseFloat(totalSuscripcionesAnuales.value) +
        parseFloat(totalSuscripcionesTrimestrales.value)
      ).toFixed(2);
    });

    // Función para mostrar el desglose de gastos del mes actual
    const mostrarDesgloseGastosMes = () => {
      // Reiniciar el modo de desglose
      modoDesglose.value = 'mesActual';
      diasAdicionalesActuales.value = 0;
      
      tituloDesglose.value = `Desglose de Gastos de ${new Date().toLocaleString('es-ES', { month: 'long', year: 'numeric' })}`;
      filtroCategoria.value = ''; // Resetear filtro
      
      // Filtrar los gastos del mes actual
      const gastosMesArray = gastos.value.filter(g => {
        const fecha = new Date(g.fecha);
        return fecha.getMonth() === mesActual && 
               fecha.getFullYear() === anoActual;
      });
      
      console.log(`Gastos del mes encontrados: ${gastosMesArray.length}`);
      
      // Ordenar por fecha (más reciente primero)
      gastosDesgloseCompleto.value = [...gastosMesArray].sort((a, b) => 
        new Date(b.fecha) - new Date(a.fecha)
      );
      
      console.log(`Gastos en gastosDesgloseCompleto: ${gastosDesgloseCompleto.value.length}`);
      
      // Calcular el total
      totalDesglose.value = gastosMesArray
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
      
      // Mostrar el modal
      if (modalDesglose) {
        modalDesglose.show();
      }
    };
    
    // Función para mostrar el desglose de gastos del año actual
    const mostrarDesgloseGastosAno = () => {
      // Reiniciar el modo de desglose
      modoDesglose.value = 'ultimoAnoNatural';
      diasAdicionalesActuales.value = 0;
      ordenDesgloseAno.value = 'importe_desc';
      
      tituloDesglose.value = `Desglose de gastos del último año natural (${inicioUltimoAnoNatural.toLocaleDateString('es-ES')} - ${finUltimoAnoNatural.toLocaleDateString('es-ES')})`;
      filtroCategoria.value = ''; // Resetear filtro
      
      // Filtrar los gastos del último año natural
      const gastosAnoArray = gastos.value.filter(g => {
        return gastoEnRango(g, inicioUltimoAnoNatural, finUltimoAnoNatural);
      });
      
      console.log(`Gastos del año encontrados: ${gastosAnoArray.length}`);
      
      // El orden final se aplica en el computed de filtrado/ordenación.
      gastosDesgloseCompleto.value = [...gastosAnoArray];
      
      console.log(`Gastos en gastosDesgloseCompleto: ${gastosDesgloseCompleto.value.length}`);
      
      // Calcular el total
      totalDesglose.value = gastosAnoArray
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
      
      // Mostrar el modal
      if (modalDesglose) {
        modalDesglose.show();
      }
    };
    
    // Función para mostrar el desglose de gastos del mes pasado
    const mostrarDesgloseGastosMesPasado = () => {
      // Reiniciar el contador si es una llamada inicial (no desde el botón de días adicionales)
      if (modoDesglose.value !== 'mesPasado') {
        diasAdicionalesActuales.value = 0;
        modoDesglose.value = 'mesPasado';
      }
      
      const { mes, ano } = mesComparativaParsed.value;
      const fechaMesComparativa = new Date(ano, mes, 1);
      const nombreMes = fechaMesComparativa.toLocaleString('es-ES', { month: 'long' });
      const nombreMesCapitalizado = nombreMes.charAt(0).toUpperCase() + nombreMes.slice(1);
      
      const diasLimite = diaActual + diasAdicionalesActuales.value;
      const textoAdicional = diasAdicionalesActuales.value > 0 ? ` (+${diasAdicionalesActuales.value} días)` : '';
      
      tituloDesglose.value = `Desglose de Gastos de ${nombreMesCapitalizado} ${ano} (hasta día ${diasLimite})${textoAdicional}`;
      filtroCategoria.value = '';
      
      const gastosMesPasadoArray = gastos.value.filter(g => gastoEnMesComparativa(g, diasLimite));
      
      console.log(`Gastos del mes pasado encontrados (con ${diasAdicionalesActuales.value} días adicionales): ${gastosMesPasadoArray.length}`);
      
      // Ordenar por fecha (más reciente primero)
      gastosDesgloseCompleto.value = [...gastosMesPasadoArray].sort((a, b) => 
        new Date(b.fecha) - new Date(a.fecha)
      );
      
      console.log(`Gastos en gastosDesgloseCompleto: ${gastosDesgloseCompleto.value.length}`);
      
      // Calcular el total
      totalDesglose.value = gastosMesPasadoArray
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
      
      // Mostrar el modal
      if (modalDesglose) {
        modalDesglose.show();
      }
    };
    
    // Función para mostrar el desglose de gastos del mismo mes del año pasado
    const mostrarDesgloseGastosMesAnoPasado = () => {
      // Reiniciar el contador si es una llamada inicial (no desde el botón de días adicionales)
      if (modoDesglose.value !== 'mesAnoPasado') {
        diasAdicionalesActuales.value = 0;
        modoDesglose.value = 'mesAnoPasado';
      }
      
      const anoPasado = anoActual - 1;
      
      // Crear una fecha para el primer día del mes actual pero del año pasado
      const fechaMesAnoPasado = new Date(anoPasado, mesActual, 1);
      const nombreMes = fechaMesAnoPasado.toLocaleString('es-ES', { month: 'long' });
      
      const diasLimite = diaActual + diasAdicionalesActuales.value;
      const textoAdicional = diasAdicionalesActuales.value > 0 ? ` (+${diasAdicionalesActuales.value} días)` : '';
      
      tituloDesglose.value = `Desglose de Gastos de ${nombreMes} ${anoPasado} (hasta día ${diasLimite})${textoAdicional}`;
      filtroCategoria.value = ''; // Resetear filtro
      
      // Filtrar los gastos del mismo mes del año pasado hasta el día actual + días adicionales
      const gastosMesAnoPasadoArray = gastos.value.filter(g => {
        const fecha = new Date(g.fecha);
        return fecha.getMonth() === mesActual && 
               fecha.getFullYear() === anoPasado &&
               fecha.getDate() <= diasLimite;
      });
      
      console.log(`Gastos del mismo mes del año pasado encontrados (con ${diasAdicionalesActuales.value} días adicionales): ${gastosMesAnoPasadoArray.length}`);
      
      // Ordenar por fecha (más reciente primero)
      gastosDesgloseCompleto.value = [...gastosMesAnoPasadoArray].sort((a, b) => 
        new Date(b.fecha) - new Date(a.fecha)
      );
      
      console.log(`Gastos en gastosDesgloseCompleto: ${gastosDesgloseCompleto.value.length}`);
      
      // Calcular el total
      totalDesglose.value = gastosMesAnoPasadoArray
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
      
      // Mostrar el modal
      if (modalDesglose) {
        modalDesglose.show();
      }
    };

    // Función para mostrar el desglose de suscripciones
    const mostrarDesgloseSuscripciones = () => {
      // Reiniciar el modo de desglose
      modoDesglose.value = 'suscripciones';
      diasAdicionalesActuales.value = 0;
      
      tituloDesglose.value = 'Desglose de Suscripciones Mensuales';
      filtroCategoria.value = ''; // Resetear filtro
      
      // Preparar las suscripciones para mostrar en el desglose
      const suscripcionesArray = suscripciones.value
        .filter(s => s.activa)
        .map(s => {
          // Calcular el importe mensual para cada suscripción
          let importeMensual = calcularCantidadMensual(s);
          
          return {
            id: s.id,
            concepto: s.concepto || 'Sin concepto',
            categoria_nombre: s.categoria_nombre || 'Sin categoría',
            fecha: s.proximo_pago, // Usamos próximo pago como fecha para ordenar
            cantidad_total: importeMensual.toFixed(2),
            periodicidad: s.periodicidad,
            tipo_division: s.tipo_division,
            cantidad_original: s.cantidad,
            dia_mes: s.dia_mes
          };
        })
        .sort((a, b) => parseFloat(b.cantidad_total) - parseFloat(a.cantidad_total)); // Ordenar de mayor a menor
      
      // Asignar al array de desglose
      gastosDesgloseCompleto.value = suscripcionesArray;
      
      // Calcular el total
      totalDesglose.value = suscripcionesArray
        .reduce((acc, s) => acc + parseFloat(s.cantidad_total), 0)
        .toFixed(2);
      
      // Mostrar el modal
      if (modalDesglose) {
        modalDesglose.show();
      }
    };

    const loadData = async () => {
      try {
        error.value = null;
        await store.dispatch('fetchGastos');
        const { data } = await axios.get('/api/suscripciones?todas=true');
        suscripciones.value = data;
        if (hasIngresos.value) {
          try {
            const resIng = await axios.get('/api/ingresos');
            ingresos.value = resIng.data;
          } catch { ingresos.value = []; }
        }
      } catch (err) {
        console.error('Error:', err);
        error.value = 'Error al cargar los datos';
      }
    };

    onMounted(() => {
      loadData();
      
      // Inicializar tooltips de Bootstrap después de que el DOM esté listo
      setTimeout(() => {
        const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        tooltipTriggerList.map(function (tooltipTriggerEl) {
          return new bootstrap.Tooltip(tooltipTriggerEl);
        });
        
        // Inicializar el modal
        const modalElement = document.getElementById('desgloseModal');
        if (modalElement) {
          modalDesglose = new bootstrap.Modal(modalElement);
        }
        
        // Verificar que los gastos se han cargado correctamente
        console.log(`Total de gastos cargados en onMounted: ${gastos.value.length}`);
        console.log(`Total del mes actual: ${totalMes.value}`);
        console.log(`Total del año actual: ${totalAno.value}`);
      }, 500);
    });

    // Categorías disponibles para el filtro
    const categoriasDisponibles = computed(() => {
      const categorias = new Set();
      gastosDesgloseCompleto.value.forEach(gasto => {
        if (gasto.categoria_nombre) {
          categorias.add(gasto.categoria_nombre);
        }
      });
      return Array.from(categorias).sort();
    });
    
    // Gastos filtrados por categoría
    const gastosDesgloseFiltrados = computed(() => {
      console.log(`Filtrando por categoría: ${filtroCategoria.value || 'Todas'}`);
      console.log(`Total de gastos antes de filtrar: ${gastosDesgloseCompleto.value.length}`);

      let gastosFiltrados = !filtroCategoria.value
        ? [...gastosDesgloseCompleto.value]
        : gastosDesgloseCompleto.value.filter(gasto => gasto.categoria_nombre === filtroCategoria.value);

      if (modoDesglose.value === 'ultimoAnoNatural') {
        if (ordenDesgloseAno.value === 'fecha_asc') {
          gastosFiltrados.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
        } else if (ordenDesgloseAno.value === 'fecha_desc') {
          gastosFiltrados.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
        } else {
          gastosFiltrados.sort((a, b) => parseFloat(b.cantidad_total) - parseFloat(a.cantidad_total));
        }
      }

      console.log(`Total de gastos después de filtrar: ${gastosFiltrados.length}`);
      return gastosFiltrados;
    });
    
    // Total de gastos filtrados
    const totalDesgloseFiltrado = computed(() => {
      return gastosDesgloseFiltrados.value
        .reduce((acc, g) => acc + parseFloat(g.cantidad_total), 0)
        .toFixed(2);
    });

    // Función para mostrar días adicionales
    const mostrarDiasAdicionales = () => {
      diasAdicionalesActuales.value += 10;
      
      // Obtener el último día del mes correspondiente
      let ultimoDiaMes;
      
      if (modoDesglose.value === 'mesPasado') {
        const { mes, ano } = mesComparativaParsed.value;
        ultimoDiaMes = new Date(ano, mes + 1, 0).getDate();
      } else if (modoDesglose.value === 'mesAnoPasado') {
        const anoPasado = anoActual - 1;
        ultimoDiaMes = new Date(anoPasado, mesActual + 1, 0).getDate();
      } else {
        ultimoDiaMes = 31; // Valor por defecto
      }
      
      // Limitar los días adicionales para no exceder el último día del mes
      if (diaActual + diasAdicionalesActuales.value > ultimoDiaMes) {
        diasAdicionalesActuales.value = ultimoDiaMes - diaActual;
      }
      
      // Si ya hemos llegado al final del mes, no hacer nada
      if (diasAdicionalesActuales.value <= 0) {
        diasAdicionalesActuales.value = 0;
        return;
      }
      
      if (modoDesglose.value === 'mesPasado') {
        mostrarDesgloseGastosMesPasado();
      } else if (modoDesglose.value === 'mesAnoPasado') {
        mostrarDesgloseGastosMesAnoPasado();
      }
    };

    // Función para capitalizar la primera letra de un string
    const capitalizarPrimeraLetra = (str) => {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const mostrarMensajeSync = (texto, tipo = 'ok') => {
      mensajeSync.value = texto;
      mensajeSyncTipo.value = tipo;
      if (mensajeSyncTimer) clearTimeout(mensajeSyncTimer);
      mensajeSyncTimer = setTimeout(() => {
        mensajeSync.value = '';
      }, 8000);
    };

    const sincronizarServicios = async () => {
      if (sincronizandoServicios.value) return;
      sincronizandoServicios.value = true;
      mensajeSync.value = '';
      try {
        const { data } = await axios.post('/api/admin/sincronizar-servicios', {}, { timeout: 300000 });
        const errores = data?.errores?.length || 0;
        if (errores) {
          const detalle = data.errores.map((e) => e.servicio).join(', ');
          mostrarMensajeSync(
            `Sincronización parcial: fallaron ${errores} servicio(s) (${detalle}).`,
            'error'
          );
        } else {
          const secs = Math.round((data.durationMs || 0) / 1000);
          mostrarMensajeSync(`Servicios sincronizados correctamente${secs ? ` (${secs}s)` : ''}.`);
        }
      } catch (e) {
        const msg =
          e.response?.data?.message ||
          e.response?.data?.error ||
          e.message ||
          'Error al sincronizar servicios';
        mostrarMensajeSync(msg, 'error');
      } finally {
        sincronizandoServicios.value = false;
      }
    };

    return {
      // Datos
      gastos,
      error,
      diaActual,
      isAdmin,
      sincronizandoServicios,
      mensajeSync,
      mensajeSyncTipo,
      sincronizarServicios,
      mesComparativa,
      etiquetaMesComparativa,
      rangoAnoPasadoFormateado,
      // Funciones de utilidad
      getBadgeClass,
      getProgressClass,
      getProgressWidth,
      getTrendTextClass,
      getPorcentaje,
      getPorcentajeInverso,
      textoVariacion,
      formatDate,
      capitalizarPrimeraLetra,
      // Totales de gastos
      totalMes,
      totalMesPuntual,
      totalMesSuscripciones,
      totalMesPasado,
      totalMesPasadoPuntual,
      totalMesPasadoSuscripciones,
      totalMesAnoPasado,
      totalMesAnoPasadoPuntual,
      totalMesAnoPasadoSuscripciones,
      totalAno,
      totalAnoMensual,
      totalAnoPasado,
      // Totales de suscripciones
      totalMensualSuscripciones,
      totalSuscripcionesMensuales,
      totalSuscripcionesAnuales,
      totalSuscripcionesTrimestrales,
      // Ingresos
      hasIngresos,
      clasesOrdenResumen,
      totalIngresosMes,
      totalIngresosAno,
      balanceMes,
      balanceAno,
      balanceMesClass,
      balanceAnoClass,
      balanceMesTextClass,
      balanceAnoTextClass,
      // Desglose de gastos
      tituloDesglose,
      totalDesglose,
      mostrarDesgloseGastosMes,
      mostrarDesgloseGastosAno,
      mostrarDesgloseGastosMesPasado,
      mostrarDesgloseGastosMesAnoPasado,
      mostrarDesgloseSuscripciones,
      // Filtro de gastos
      filtroCategoria,
      ordenDesgloseAno,
      gastosDesgloseCompleto,
      gastosDesgloseFiltrados,
      totalDesgloseFiltrado,
      categoriasDisponibles,
      // Variables para controlar el modo de desglose y días adicionales
      modoDesglose,
      diasAdicionalesActuales,
      mostrarDiasAdicionales,
      // Variable para controlar si se está mostrando el mes completo
      mostrandoMesCompleto
    };
  }
};
</script>

<style scoped>
.badge {
  font-size: 1rem;
  padding: 0.5rem 1rem;
}

.list-group-item {
  border-left: none;
  border-right: none;
}

/* Estilo para elementos clicables */
.list-group-item[style*="cursor: pointer"] {
  transition: background-color 0.3s ease;
}

.list-group-item[style*="cursor: pointer"]:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

.list-group-item[style*="cursor: pointer"]:active {
  background-color: rgba(0, 123, 255, 0.1);
}

.list-group-item[style*="cursor: pointer"] .badge {
  transition: transform 0.2s ease;
}

.list-group-item[style*="cursor: pointer"]:hover .badge {
  transform: scale(1.05);
}

.progress {
  background-color: #e9ecef;
  border-radius: 0.25rem;
}

.mes-comparativa-input {
  width: auto;
  max-width: 9.5rem;
  cursor: pointer;
}

/* Estilo para los elementos con tooltip */
[data-bs-toggle="tooltip"] {
  cursor: help;
}

/* Estilos para el modal de desglose */
.modal-body {
  max-height: 70vh;
  overflow-y: auto;
}

.table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 123, 255, 0.05);
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.1);
}

.resumen-title {
  font-size: 1.85rem;
  letter-spacing: -0.03em;
}

.resumen-hero {
  margin-bottom: 1.5rem;
  padding-bottom: 0.25rem;
}

.resumen-kicker {
  margin-bottom: 0.15rem;
  color: var(--tinta-suave, #5c675e);
  font-size: 0.85rem;
}

.resumen-cifra {
  margin-bottom: 0.2rem;
  font-size: clamp(2.4rem, 6vw, 3.4rem);
  line-height: 1;
  letter-spacing: -0.03em;
}

.resumen-hero-sub {
  margin-bottom: 0;
  color: var(--tinta, #1c241c);
}

.resumen-bloque-titulo {
  margin-bottom: 0.85rem;
  font-size: 1.05rem;
  font-weight: 650;
}

.resumen-importe,
.resumen-total-ano,
.resumen-delta {
  font-variant-numeric: tabular-nums;
}

.cifra-gasto {
  color: var(--gasto, #9a3412);
}

.cifra-ingreso {
  color: var(--ingreso, #1f6b45);
}

.resumen-delta {
  margin-left: 0.35rem;
  font-size: 0.85em;
}

.resumen-card {
  background: var(--superficie, #fbfcf9);
  border-color: var(--linea, rgba(22, 53, 40, 0.12));
}

.resumen-total-ano {
  color: var(--gasto, #9a3412);
  font-weight: 650;
  font-size: 1.05rem;
}
</style> 